import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { getSearchQueryParamsForAPI } from 'src/utils/url.helpers';
import { NOTIFICATIONS } from './constants';

export interface Notification {
  id: string | number;
  category: string;
  severity: string;
  title: string;
  body: string;
  source_content_type: string;
  source_object_id: number;
  read_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

interface NotificationParams {
  page?: number;
  pageSize?: number;
  [key: string]: unknown;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [NOTIFICATIONS],
  endpoints: (builder) => ({
    getNotifications: builder.query<
      Notification[],
      NotificationParams | void
    >({
      query: (params) => {
        const queryParams = params
          ? getSearchQueryParamsForAPI(params)
          : '';
        return {
          url: `v1/events/notifications/${queryParams}`,
          method: 'GET',
        };
      },
      transformResponse: (response: PaginatedResponse<Notification>) =>
        response.results,
      providesTags: [{ type: NOTIFICATIONS, id: 'LIST' }],
    }),
    getNotificationById: builder.query<Notification, number>({
      query: (id) => ({
        url: `v1/events/notifications/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: NOTIFICATIONS, id }],
    }),
    getUnreadCount: builder.query<{ count: number }, void>({
      query: () => ({
        url: 'v1/events/notifications/unread-count/',
        method: 'GET',
      }),
      providesTags: [{ type: NOTIFICATIONS, id: 'UNREAD_COUNT' }],
    }),
    bulkAction: builder.mutation<
      void,
      { action: 'mark-read' | 'archive' | 'delete'; ids?: number[] }
    >({
      query: (data) => ({
        url: 'v1/events/notifications/actions/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: NOTIFICATIONS, id: 'LIST' },
        { type: NOTIFICATIONS, id: 'UNREAD_COUNT' },
      ],
    }),
    deleteNotification: builder.mutation<void, number>({
      query: (id) => ({
        url: `v1/events/notifications/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: NOTIFICATIONS, id: 'LIST' },
        { type: NOTIFICATIONS, id: 'UNREAD_COUNT' },
      ],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetNotificationByIdQuery,
  useGetUnreadCountQuery,
  useBulkActionMutation,
  useDeleteNotificationMutation,
} = notificationsApi;
